<script setup lang="ts">
import type { Ref } from 'vue'
import { useAuctionStore } from '@autobid/nuxt-pinia-store/store/useAuctionStore'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useAutoAnimate } from '@formkit/auto-animate/vue'
import { TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import { useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import AuctionListFilterHeader from './AuctionListFilterHeader.vue'
import AuctionListFilterTab from './AuctionListFilterTab.vue'
import AuctionListFilterChips from './chips/AuctionListFilterChips.vue'
import AuctionBrand from './filters/AuctionBrand.vue'
import AuctionCountry from './filters/AuctionCountry.vue'
import AuctionDate from './filters/AuctionDate.vue'
import AuctionName from './filters/AuctionName.vue'
import AuctionNumber from './filters/AuctionNumber.vue'
import AuctionStatus from './filters/AuctionStatus.vue'
import AuctionType from './filters/AuctionType.vue'
import CarType from './filters/CarType.vue'

const appIds = inject<Ref<number[]>>('appIds')

const items = [
  {
    title: 'auction-list.auction-type',
    component: AuctionType,
  },
  {
    title: 'auction-list.auction-date',
    component: AuctionDate,
  },
  {
    title: 'auction-list.auction-country',
    component: AuctionCountry,
  },
  {
    title: 'auction-list.car-type',
    component: CarType,
  },
  {
    title: 'auction-list.auction-name',
    component: AuctionName,
  },
  {
    title: 'auction-list.auction-number',
    component: AuctionNumber,
  },
  {
    title: 'auction-list.auction-status',
    component: AuctionStatus,
  },
  {
    title: 'auction-list.auction-brand',
    component: AuctionBrand,
  },
]

const [parent] = useAutoAnimate()

const auctionStoreKey = inject('auctionStoreKey')
const { isOpen } = storeToRefs(useAuctionStore(String(auctionStoreKey)))

const { locale } = useI18n()
const { $trpc } = useNuxtApp()

const { data } = useQuery(
  ['auctionListFilter', locale.value, appIds],
  async () => {
    try {
      return await $trpc.auctions.getAuctionsFilter.query({
        lang: locale.value,
        appIds: appIds?.value?.length ? appIds.value : undefined,
      })
    }
    catch (error) {
      const { message, code, handled } = getError(error)

      if (!handled) {
        throw createError({ statusCode: code, statusMessage: message })
      }
    }
  },
)

provide('filterData', data)

const showFilter = computed(() => {
  return (
    Object.values(data.value ?? {})
      .map((item) => {
        if (Array.isArray(item)) {
          return item.length
        }
        return 1
      })
      .reduce((acc, curr) => acc + curr, 0) > 0
  )
})
</script>

<template>
  <div
    v-if="showFilter"
    ref="parent"
    class="auction-list-filter ml-auto flex w-full flex-col items-start p-2"
  >
    <AuctionListFilterHeader />
    <div v-if="data && isOpen" class="w-full">
      <TabGroup v-slot="{ selectedIndex }">
        <TabList class="flex flex-col @md:flex-row">
          <AuctionListFilterTab
            v-for="(item, index) in items"
            :key="`tab-list-item-${item.title}`"
          >
            <template #header>
              <span
                class="text-lg font-semibold @md:text-base @md:font-normal"
              >
                {{ $t(item.title) }}
              </span>
            </template>
            <template #content>
              <div
                v-if="selectedIndex === index"
                class="w-full bg-white p-2 @md:hidden"
              >
                <Component :is="item.component" />
              </div>
            </template>
          </AuctionListFilterTab>
        </TabList>

        <TabPanels class="-mt-2 hidden @md:block">
          <TabPanel
            v-for="item in items"
            :key="`tab-panel-item-${item.title}`"
          >
            <Component :is="item.component" />
          </TabPanel>
        </TabPanels>
        <AuctionListFilterChips />
      </TabGroup>
    </div>
  </div>
</template>

<style lang="scss">
.auction-list-filter {
  * {
    user-select: none;
  }
}
</style>
